import { isServer } from '@mr-yum/frontend-core/dist/support/env'
import { isToday, parseISO } from 'date-fns'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useQuery } from 'urql'

import { ClientOnly } from '@/components/Common/ClientOnly'
import { RewardMembershipCTA } from '@/components/LoyaltyMembership/LoyaltyBanners'
import { useEonxEnvContext } from '@/contexts/EonxEnvContext'
import {
  useOrderingTypeContext,
  useVenueContext,
} from '@/contexts/VenueOrderContext'
import {
  CartLandingQuery,
  CurrentUserQuery,
  ListOpenGroupOrdersForVenueQuery,
  OrderingType,
  VenueFieldRequirement,
  VenueLandingQuery,
  VenueListMembershipsDocument,
  VenueOrderingWindowsDocument,
} from '@/gql/graphql'
import { routes } from '@/lib/routes'

import { AddressButton } from './AddressButton'
import { ManageTabBlock } from './Blocks/ManageTabBlock'
import { OrderingTypeButton } from './OrderingTypeButton'
import { OrderingWindowButton } from './OrderingWindowButton'
import { TableNumberButton } from './TableNumberButton'
import { useGetNextSheet } from './utils/sheetUtils'
import {
  findSettingsByOrderingType,
  venueToOrderingTypeItems,
} from './utils/venueUtils'

interface Props {
  venue: NonNullable<VenueLandingQuery['guestVenue']>
  orderingTypePrompt: boolean
  cart: CartLandingQuery['getCart']
  cartFetching: boolean
  cartError?: Error | null
  debouncedCategorySearch: string
  userDataFetching: boolean
  isActionBlockLoading: boolean
  showManageTabBlock: boolean
  showBlock: Error | ListOpenGroupOrdersForVenueQuery | undefined
  tabs: { id: string }[]
  userData: CurrentUserQuery | undefined
}

export const TableAndDiningTypeSelection = ({
  venue,
  cart,
  cartFetching,
  cartError,
  debouncedCategorySearch,
  userDataFetching,
  isActionBlockLoading,
  showManageTabBlock,
  showBlock,
  tabs,
  userData,
}: Props) => {
  const { orderingType, orderingTypeSlug } = useOrderingTypeContext()
  const { venueSlug } = useVenueContext()
  const { isEonxEnv } = useEonxEnvContext()

  const intl = useIntl()

  const [{ data: venueMembershipData, fetching: venueMembershipDataFetching }] =
    useQuery({
      query: VenueListMembershipsDocument,
      pause: isServer || !venue?.id || userDataFetching,
      variables: {
        venueId: venue?.id ?? '',
        guestId: userData?.currentUser?.id,
        guestMobile: userData?.currentUser?.mobile,
      },
      requestPolicy: 'cache-and-network',
    })

  const settings = findSettingsByOrderingType({
    venue,
    orderingType,
  })

  const tableIsOff = useMemo(() => {
    if (!cart) {
      return false
    }

    return cart.cartValidationErrorsV2.some(
      (value) => value.type === 'TABLE_NUMBER_ALL_OFF',
    )
  }, [cart])

  const { nonMenuCount, items } = venueToOrderingTypeItems(
    venue,
    orderingType,
    intl,
  )

  const [{ data: venueOrderingWindowsData }] = useQuery({
    query: VenueOrderingWindowsDocument,
    variables: { venueSlug, orderingType },
  })

  const asapSettings =
    venueOrderingWindowsData?.venue?.orderingTypeSettingsV2?.orderingWindow
      ?.asapWindowEnabled

  const { addPaths, editPaths } = useGetNextSheet({
    cart,
    asapEnabled: asapSettings,
    venue,
    orderingTypeSlug,
  })

  const firstOrderingWindow =
    venueOrderingWindowsData?.venue?.orderingTypeSettingsV2?.orderingWindow?.availableOrderingWindows.find(
      (availableOrderingWindow) => availableOrderingWindow.isPast === false,
    )

  const today = firstOrderingWindow?.orderingWindowStartDate
    ? isToday(parseISO(firstOrderingWindow?.orderingWindowStartDate))
    : false

  if (nonMenuCount === 0) {
    return null
  }

  if (cartFetching || !cart) {
    return <div className="h-[20px] w-[220px] rounded skeleton" />
  }

  const startDate = cart?.orderingWindowStartDate

  const isVisualMenu = orderingType === OrderingType.Menu
  const isNonVisualMenu = !isVisualMenu && !cartError

  const hasNoPaths = addPaths.length === 0 && editPaths.length === 0
  const hasTableNumberPaths = addPaths.includes('table-number')
  const isAsap = asapSettings && !startDate && today
  const tableNumber = cart?.tableNumber

  const orderingWindowEnabled = settings?.orderingWindow?.enabled ?? false
  const isTableNumberHidden =
    settings?.hasTableNumber === VenueFieldRequirement.Hidden

  const availableForOrderingType = [
    OrderingType.DineIn,
    OrderingType.Counter,
  ].includes(orderingType)

  const hasTab =
    !!venue?.startTabAvailable && availableForOrderingType && !isEonxEnv

  const count = [hasTab].filter(Boolean).length
  const showManageBlock = showBlock && showManageTabBlock

  const hasMultipleOrderingTypes = items.length > 1
  const hasNoOrderingTypeButton =
    !hasMultipleOrderingTypes && orderingType === OrderingType.DineIn

  return (
    <>
      <div className="flex w-full flex-col items-start gap-1">
        <div className="flex flex-wrap items-start gap-y-1 dot-between">
          {!hasNoOrderingTypeButton && (
            <OrderingTypeButton
              venue={venue}
              hasMultipleOrderingTypes={hasMultipleOrderingTypes}
            />
          )}
          {isNonVisualMenu && orderingWindowEnabled && (
            <OrderingWindowButton
              orderingType={orderingType}
              orderingWindowEndDate={cart?.orderingWindowEndDate}
              startDate={startDate}
              hasNoPaths={hasNoPaths}
              isAsap={isAsap}
            />
          )}

          {isNonVisualMenu && !isTableNumberHidden && (
            <TableNumberButton
              isTableTurnedOff={tableIsOff}
              cart={cart}
              tableShortText={settings?.tableShortText}
              hasNoPaths={hasNoPaths}
              tableNumber={tableNumber}
              hasTableNumberPaths={hasTableNumberPaths}
            />
          )}
        </div>
        {isNonVisualMenu && (
          <AddressButton cart={cart} hasNoPaths={hasNoPaths} />
        )}
      </div>
      <div className="w-full">
        <ClientOnly>
          {debouncedCategorySearch === '' && (
            <ManageTabBlock
              venueSlug={venueSlug}
              count={count}
              tabId={tabs[0]?.id}
              showManageBlock={showManageBlock}
              isActionBlockLoading={isActionBlockLoading}
            />
          )}

          {!venueMembershipDataFetching &&
            !userDataFetching &&
            venueMembershipData?.listVenueMemberships?.map((program) => {
              return (
                program?.configuration?.cta?.['link'] &&
                !program.membership.id && (
                  <RewardMembershipCTA
                    key={program.id}
                    venueName={venue.name}
                    program={program}
                    cta="link"
                    href={routes.rewardLinkMembership.toPath!({
                      venueSlug,
                      programId: program.id,
                    })}
                  />
                )
              )
            })}
        </ClientOnly>
      </div>
    </>
  )
}
